import { Link as UnstyledLink } from "react-router-dom";
import styled from "styled-components";

import { Avatar as AvatarDs } from "ds/Avatar";
import { Link as DSLink } from "ds/Button";
import * as Dropdown from "ds/Dropdown";
import { Tooltip } from "ds/Tooltip";
import { semiBoldAlias } from "Libs/theme";

const saneBoxSizing = `
  box-sizing: border-box;

  & * {
    box-sizing: border-box;
  }
`;

export const Note = styled.div`
  margin-left: auto;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;

  color: var(--label-granite-color, var(--granite));
`;

export const ActivityListItemLayout = styled.div<{
  withShadow?: boolean;
  isDeletingStatus?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  border-radius: var(
    --activity-section-border-radius,
    var(--section-2px-border-radius, var(--2px))
  );
  background-color: var(--section-snow-background-color, var(--snow));
  box-shadow: ${props =>
    props.withShadow
      ? `var(--box-shadow)
    var(--menu-ebony-shadow-box-shadow-color,var(--ebony-shadow))`
      : null};
  opacity: ${props => props.isDeletingStatus && `0.5`};

  &:hover,
  &:focus-within {
    box-shadow: var(--menu-box-shadow-box-shadow, var(--box-shadow))
      // @ts-ignore
      var(--menu-ebony-shadow-box-shadow-color, var(--ebony-shadow));

    background-color: var(
      --activity-list-item-hover-section-background-color,
      var(--section-snow-background-color, var(--snow))
    );
  }
`;

export const Wrapper = styled.div<{ $shouldShowLog?: boolean }>`
  ${saneBoxSizing};
  color: var(--section-ebony-light-color, var(--ebony-light));
  cursor: ${props => (props.$shouldShowLog ? "pointer" : "inherit")};
  padding: 16px;
`;

export const Content = styled.div`
  display: flex;
  align-items: center;
`;

export const Icon = styled.div`
  margin-right: 16px;
  line-height: 0;
  color: var(--icon-slate-color, var(--slate));

  min-width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    .fill-target {
      fill: var(--icon-slate-fill, var(--slate));
    }
    .stroke-target {
      stroke: var(--icon-slate-stroke, var(--slate));
    }
  }
`;

export const Avatar = styled(AvatarDs)`
  margin-right: 24px;
  display: none;
  min-width: 24px;
  @media (min-width: 768px) {
    display: inline-block;
  }
`;

// This div comes as HTML from the API
// That's why we use nested styles here
export const Event = styled.div`
  flex-grow: 1;
  width: 495px;
  line-height: 14px;
  overflow: hidden;
  padding-right: 16px;

  strong a {
    font-weight: 700;
    text-decoration: none;
    color: inherit;
  }

  strong a:hover,
  strong a:focus {
    color: var(--link-color-primary-600-color, var(--color-primary-600));
  }
`;

export const Link = styled(UnstyledLink)`
  ${semiBoldAlias()};
  padding-right: 10px;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  color: var(--link-color-primary-600-color, var(--color-primary-600));

  &:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  @media (min-width: 768px) {
    display: inline-block;
    padding-right: 0;
    padding-left: 10px;
    line-height: 1.4;
  }
`;

export const DetailsWrapper = styled.div`
  font-size: 12px;
  color: var(--section-granite-color, var(--granite));
  display: flex;
  line-height: 18px;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
`;

export const EnvironmentLink = styled(UnstyledLink)`
  ${semiBoldAlias()};
  display: inline-block;
  font-size: 12px;
  text-decoration: none;
  margin-right: 8px;

  &:hover,
  &:focus {
    color: var(--link-color-primary-600-color, var(--color-primary-600));
  }
`;

export const TextWrapper = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Sha = styled.span``;

export const Message = styled.span`
  margin-left: 8px;
`;

export const ActivityStatus = styled.span`
  ${semiBoldAlias()};
  margin-left: 42px;
  font-size: 12px;
  color: var(--section-granite-color, var(--granite));

  @media (min-width: 768px) {
    font-size: 13px;
    margin-right: 16px;
  }
`;

export const AcitivityStatusWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const ActivityDate = styled.span`
  ${semiBoldAlias()};
  min-width: 125px;
  margin-left: 16px;
  font-size: 12px;
  text-align: left;
  color: var(--section-granite-color, var(--granite));

  @media (min-width: 768px) {
    font-size: 13px;
    margin-right: 16px;
  }
`;

export const Duration = styled.span`
  ${semiBoldAlias()};
  min-width: 56px;
  font-size: 12px;
  text-align: left;
  color: var(--section-granite-color, var(--granite));

  @media (min-width: 768px) {
    font-size: 13px;
    margin-right: 16px;
  }
`;

export const Environment = styled(DSLink)`
  font-weight: 600;
  margin-right: 0.5rem;
  font-size: 0.75rem;
  text-decoration: none;
  border: none;
  color: var(--link-slate-color, var(--slate));
  &:hover {
    color: var(--mode-bg-primary-default);
  }
`;

export const DropdownTrigger = styled(Dropdown.Trigger)`
  border: 0;
  width: 40px;
  height: 32px;
  margin-left: 8px;
`;

export const DropdownContent = styled(Dropdown.Content)`
  z-index: 5;
  min-width: 164px;
  padding: 8px 10px;
  margin-top: 0;
`;

const TriggerLayout = styled(Tooltip.Trigger)`
  overflow: hidden;
`;

export const CancelItem = styled(Dropdown.Item)<{ disabled?: boolean }>`
  opacity: ${props => (props.disabled ? "0.5" : "1")};

  ${TriggerLayout} {
    flex: 1;
  }
`;
